import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  StatusBadge,
  destinationLifecycleTone,
  jobStatusTone,
} from "@/components/status/status-badge";
import { api } from "@/lib/api";
import {
  FilterField,
  FilterToolbar,
  PaginationBar,
  SearchInput,
  formatUnix,
  selectClassName,
} from "./shared";

type StreamRow = {
  id: string;
  name: string;
  lifecycle: string;
  accountId: string;
  organizationName: string;
  conferenceId: string;
  conferenceName: string;
  sourceProvider: string;
  destinationProvider: string;
  lastErrorCode?: string | null;
  createdAt: number;
};

type StreamDetail = {
  stream: {
    id: string;
    name: string;
    lifecycle: string;
    accountId: string;
    organizationName: string;
    conferenceId: string;
    conferenceName: string;
    sourceProvider: string;
    destinationProvider: string;
    mapping: Record<string, unknown> | null;
    lastErrorCode: string | null;
    lastSuccessAt: number | null;
    createdAt: number;
    updatedAt: number;
    scheduleIntervalSeconds: number | null;
  };
  usage: {
    trialAttendeesUsed: number;
    attendeesProcessedTotal: number;
  };
  recentJobs: Array<{
    id: string;
    status: string;
    trigger: string;
    examined: number;
    createdCount: number;
    updatedCount: number;
    skipped: number;
    failed: number;
    errorSummary: string | null;
    createdAt: number;
  }>;
};

const LIFECYCLE_ACTIONS: Array<{
  action: string;
  label: string;
  from: string[];
}> = [
  { action: "activate", label: "Activate", from: ["tested", "paused"] },
  { action: "pause", label: "Pause", from: ["active"] },
  {
    action: "archive",
    label: "Archive",
    from: ["draft", "tested", "active", "paused"],
  },
  { action: "restore", label: "Restore", from: ["archived"] },
];

export function SuperAdminStreamsPage() {
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [streams, setStreams] = useState<StreamRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (q) params.set("q", q);
    if (lifecycle) params.set("lifecycle", lifecycle);
    if (source) params.set("source", source);
    if (destination) params.set("destination", destination);
    void api<{ streams: StreamRow[]; pagination: { total: number } }>(
      `/api/platform/streams?${params}`,
    )
      .then((r) => {
        setStreams(r.streams ?? []);
        setTotal(r.pagination?.total ?? 0);
      })
      .catch(() => {
        setStreams([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [q, lifecycle, source, destination, offset]);

  const page = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Streams</h1>
        <p className="text-sm text-rs-ink-muted">
          Cross-tenant sync stream console.
        </p>
      </div>
      <FilterToolbar>
        <FilterField
          id="sa-streams-q"
          label="Search"
          className="min-w-[12rem] flex-[2] space-y-1"
        >
          <SearchInput
            id="sa-streams-q"
            value={qDraft}
            onChange={setQDraft}
            placeholder="Stream, org, or conference"
          />
        </FilterField>
        <FilterField
          id="sa-streams-lifecycle"
          label="Lifecycle"
          className="min-w-[9rem] space-y-1"
        >
          <select
            id="sa-streams-lifecycle"
            className={selectClassName}
            value={lifecycle}
            onChange={(e) => {
              setLifecycle(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Any</option>
            {["draft", "tested", "active", "paused", "archived"].map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField
          id="sa-streams-source"
          label="Source"
          className="min-w-[9rem] space-y-1"
        >
          <select
            id="sa-streams-source"
            className={selectClassName}
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Any</option>
            <option value="zoom">Zoom</option>
            <option value="google_meet">Google Meet</option>
            <option value="linkedin_events">LinkedIn Events</option>
            <option value="constant_contact">Constant Contact</option>
            <option value="heysummit">HeySummit</option>
            <option value="fluent_forms">Fluent Forms</option>
            <option value="mailchimp">Mailchimp</option>
          </select>
        </FilterField>
        <FilterField
          id="sa-streams-dest"
          label="Destination"
          className="min-w-[9rem] space-y-1"
        >
          <Input
            id="sa-streams-dest"
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
              setOffset(0);
            }}
            placeholder="e.g. neon"
          />
        </FilterField>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setQ(qDraft.trim());
            setOffset(0);
          }}
        >
          Search
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setQDraft("");
            setQ("");
            setLifecycle("");
            setSource("");
            setDestination("");
            setOffset(0);
          }}
        >
          Clear
        </Button>
      </FilterToolbar>
      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <Panel>
            <ul className="divide-y divide-rs-border">
              {streams.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/superadmin/streams/${s.id}`}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm hover:bg-rs-muted"
                  >
                    <div>
                      <p className="font-medium text-rs-ink">{s.name}</p>
                      <p className="text-rs-ink-muted">
                        {s.organizationName} · {s.conferenceName} ·{" "}
                        {s.sourceProvider} → {s.destinationProvider}
                      </p>
                    </div>
                    <StatusBadge tone={destinationLifecycleTone(s.lifecycle)}>
                      {s.lifecycle}
                    </StatusBadge>
                  </Link>
                </li>
              ))}
              {streams.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                  No streams found.
                </li>
              ) : null}
            </ul>
          </Panel>
          <PaginationBar
            page={page}
            limit={limit}
            total={total}
            onPageChange={(p) => setOffset((p - 1) * limit)}
          />
        </>
      )}
    </div>
  );
}

export function SuperAdminStreamDetailPage() {
  const { streamId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<StreamDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const r = await api<StreamDetail>(
      `/api/platform/streams/${encodeURIComponent(streamId)}`,
    );
    setData(r);
  }

  useEffect(() => {
    setLoading(true);
    void refresh()
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  if (loading) return <PageSkeleton />;
  if (!data) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-rs-ink">Stream Not Found</h1>
        <Button
          variant="secondary"
          onClick={() => navigate("/superadmin/streams")}
        >
          Back to Streams
        </Button>
      </div>
    );
  }

  const { stream, usage, recentJobs } = data;
  const availableActions = LIFECYCLE_ACTIONS.filter((a) =>
    a.from.includes(stream.lifecycle),
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-rs-ink-muted">
            <Link
              className="text-rs-accent hover:underline"
              to="/superadmin/streams"
            >
              Streams
            </Link>
            {" / "}
            {stream.name}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-rs-ink">{stream.name}</h1>
            <StatusBadge tone={destinationLifecycleTone(stream.lifecycle)}>
              {stream.lifecycle}
            </StatusBadge>
          </div>
          <p className="text-sm text-rs-ink-muted">
            <Link
              className="text-rs-accent hover:underline"
              to={`/superadmin/accounts/${stream.accountId}`}
            >
              {stream.organizationName}
            </Link>
            {" · "}
            {stream.conferenceName}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {availableActions.map((a) => (
            <Button
              key={a.action}
              type="button"
              variant={a.action === "archive" ? "danger" : "secondary"}
              onClick={() => setAction(a.action)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Providers</p>
          <p className="mt-1 font-medium text-rs-ink">
            {stream.sourceProvider} → {stream.destinationProvider}
          </p>
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Trial Attendees Used</p>
          <p className="mt-1 text-xl font-semibold text-rs-ink">
            {usage.trialAttendeesUsed.toLocaleString()}
          </p>
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Attendees Processed</p>
          <p className="mt-1 text-xl font-semibold text-rs-ink">
            {usage.attendeesProcessedTotal.toLocaleString()}
          </p>
        </Panel>
      </div>

      <Panel className="p-5">
        <h2 className="font-semibold text-rs-ink">Configuration Summary</h2>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-rs-ink-muted">Last Success</dt>
            <dd>{formatUnix(stream.lastSuccessAt)}</dd>
          </div>
          <div>
            <dt className="text-rs-ink-muted">Last Error</dt>
            <dd>{stream.lastErrorCode ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-rs-ink-muted">Schedule Interval</dt>
            <dd>
              {stream.scheduleIntervalSeconds != null
                ? `${stream.scheduleIntervalSeconds}s`
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-rs-ink-muted">Updated</dt>
            <dd>{formatUnix(stream.updatedAt)}</dd>
          </div>
        </dl>
        {stream.mapping ? (
          <pre className="mt-3 overflow-x-auto rounded-rs-sm bg-rs-muted p-3 text-xs text-rs-ink">
            {JSON.stringify(stream.mapping, null, 2)}
          </pre>
        ) : null}
      </Panel>

      <Panel>
        <PanelHeader title="Recent Jobs" />
        <ul className="divide-y divide-rs-border">
          {recentJobs.map((j) => (
            <li key={j.id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-rs-ink">
                    {j.trigger} · examined {j.examined} · +{j.createdCount} / ~
                    {j.updatedCount} / skip {j.skipped} / fail {j.failed}
                  </p>
                  <p className="text-rs-ink-muted">{formatUnix(j.createdAt)}</p>
                  {j.errorSummary ? (
                    <p className="text-rs-warning">{j.errorSummary}</p>
                  ) : null}
                </div>
                <StatusBadge tone={jobStatusTone(j.status)}>
                  {j.status}
                </StatusBadge>
              </div>
            </li>
          ))}
          {recentJobs.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No recent jobs.
            </li>
          ) : null}
        </ul>
      </Panel>

      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}

      <ConfirmDialog
        open={action != null}
        onOpenChange={(open) => {
          if (!open) {
            setAction(null);
            setReason("");
          }
        }}
        title={
          action === "activate"
            ? "Activate Stream"
            : action === "pause"
              ? "Pause Stream"
              : action === "archive"
                ? "Archive Stream"
                : "Restore Stream"
        }
        description={
          action === "activate"
            ? "Activating may require a billing bypass reason if the account is past due or trial-limited."
            : "This lifecycle change is audited and may affect billing quantity."
        }
        impact={
          <div className="space-y-2">
            <p>
              {stream.name}: {stream.lifecycle} → {action}
            </p>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                action === "activate"
                  ? "Reason (required if billing gate fails)"
                  : "Reason (optional)"
              }
              aria-label="Lifecycle Reason"
            />
          </div>
        }
        confirmLabel={
          action === "activate"
            ? "Activate Stream"
            : action === "pause"
              ? "Pause Stream"
              : action === "archive"
                ? "Archive Stream"
                : "Restore Stream"
        }
        dangerous={action === "archive"}
        busy={busy}
        onConfirm={async () => {
          if (!action) return;
          setBusy(true);
          try {
            await api(
              `/api/platform/streams/${encodeURIComponent(streamId)}/lifecycle`,
              {
                method: "POST",
                body: JSON.stringify({
                  action,
                  reason: reason.trim() || undefined,
                }),
              },
            );
            setAction(null);
            setReason("");
            setMessage("Lifecycle updated.");
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Lifecycle update failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}
